import axios from 'axios';

// Use backend API URL
// Set REACT_APP_API_BASE_URL to override; set to 'SAME_ORIGIN' to force relative URLs explicitly
const ENV_BASE = process.env.REACT_APP_API_BASE_URL;
export const API_BASE_URL = ENV_BASE === 'SAME_ORIGIN' ? '' : (ENV_BASE || 'https://api.sonickartnow.com');

export const getStoredAuthToken = () => (
  localStorage.getItem('token') ||
  localStorage.getItem('vendorToken') ||
  localStorage.getItem('adminToken') ||
  localStorage.getItem('vendor_token') ||
  localStorage.getItem('admin_token') ||
  ''
);

export const getSocketBaseUrl = () => {
  if (API_BASE_URL) {
    return API_BASE_URL;
  }

  if (typeof window !== 'undefined' && window.location?.origin) {
    return window.location.origin;
  }

  return '';
};

export const apiClient = axios.create({
  baseURL: API_BASE_URL,
  timeout: 15000,
  headers: {
    'Content-Type': 'application/json'
  },
  withCredentials: true
});

// Request interceptor to automatically add admin authentication headers
apiClient.interceptors.request.use(
  (config) => {
    // Add vendor authentication headers if available
    const token = localStorage.getItem('token');
    const vendorToken = localStorage.getItem('vendorToken');
    const adminToken = localStorage.getItem('adminToken');
    const adminApiKey = localStorage.getItem('adminApiKey') || localStorage.getItem('ADMIN_API_KEY');
    const userType = localStorage.getItem('userType');
    
    // Prioritize tokens based on user type
    if (userType === 'admin' && adminToken) {
      config.headers['Authorization'] = `Bearer ${adminToken}`;
    } else if (userType === 'vendor' && (vendorToken || token)) {
      config.headers['Authorization'] = `Bearer ${vendorToken || token}`;
    } else if (token) {
      config.headers['Authorization'] = `Bearer ${token}`;
    }
    
    if (adminApiKey) {
      config.headers['x-admin-key'] = adminApiKey;
    }
    
    // Add vendor ID header if available
    const vendorData = localStorage.getItem('vendorData');
    if (vendorData) {
      try {
        const parsed = JSON.parse(vendorData);
        if (parsed.id) {
          config.headers['x-vendor-id'] = parsed.id;
        }
      } catch (e) {
        console.warn('Failed to parse vendor data:', e);
      }
    }
    
    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

// Log requests/responses for easier debugging
apiClient.interceptors.response.use(
  (res) => res,
  (error) => {
    const cfg = error?.config || {};
    const url = cfg?.baseURL ? `${cfg.baseURL}${cfg.url || ''}` : (cfg.url || '');
    
    // Handle authentication errors
    if (error?.response?.status === 401) {
      console.warn('Authentication failed, clearing tokens');
      localStorage.removeItem('token');
      localStorage.removeItem('vendorToken');
      localStorage.removeItem('adminToken');
      localStorage.removeItem('vendorData');
      localStorage.setItem('isLoggedIn', 'false');
      
      // Redirect to login if not already there
      if (!window.location.pathname.includes('/login')) {
        window.location.href = '/login';
      }
    }
    
    // Handle forbidden errors
    if (error?.response?.status === 403) {
      console.warn('Access forbidden');
    }
    
    /* eslint-disable no-console */
    console.error('API Error:', {
      method: (cfg.method || 'get').toUpperCase(),
      url,
      status: error?.response?.status,
      data: error?.response?.data,
      message: error?.message
    });
    /* eslint-enable no-console */
    return Promise.reject(error);
  }
);

export const uploadClient = axios.create({
  baseURL: API_BASE_URL,
  timeout: 300000,
  withCredentials: true
});

uploadClient.interceptors.response.use(
  (res) => res,
  (error) => {
    const cfg = error?.config || {};
    const url = cfg?.baseURL ? `${cfg.baseURL}${cfg.url || ''}` : (cfg.url || '');
    /* eslint-disable no-console */
    console.error('Upload API Error:', {
      method: (cfg.method || 'post').toUpperCase(),
      url,
      status: error?.response?.status,
      data: error?.response?.data,
      message: error?.message
    });
    /* eslint-enable no-console */
    return Promise.reject(error);
  }
);

export const endpoints = {
  // Authentication Endpoints
  registerVendor: '/api/vendors/register',
  loginVendor: '/api/vendors/login',
  loginAdmin: '/api/admin/login',
  forgotPassword: '/api/vendors/forgot-password',
  resetPassword: '/api/vendors/reset-password',
  updateLocation: (id) => `/api/vendors/${id}/location`,
  uploadKyc: (id) => `/api/vendors/${id}/kyc`,
  kycStatus: (id) => `/api/vendors/${id}/kyc-status`,
  kycDocs: (id) => `/api/vendors/${id}/kyc-docs`,
  deleteKycDoc: (vendorId, docId) => `/api/vendors/${vendorId}/kyc-docs/${docId}`,
  
  // Vendor Data & Profile Endpoints
  vendorById: (id) => `/api/vendors/${id}`,
  vendorEarnings: (period) => `/api/vendor/earnings?period=${period}`,
  vendorBankDetails: '/api/vendor/bank-details',
  updateVendorBankDetails: '/api/vendor/bank-details',
  uploadVendorMedia: '/api/upload/vendor-media',
  deleteVendorMedia: (type) => `/api/upload/vendor-media?type=${type}`,
  
  // Vendor Profile Endpoints
  vendorProfile: (id) => `/api/vendors/${id}`,
  updateVendorProfile: (id) => `/api/vendors/${id}`,
  vendorDashboard: (id) => `/api/vendors/${id}/dashboard`,
  
  // Dashboard Metrics Endpoints
  weeklyRevenue: '/api/metrics/weekly-revenue',
  ordersByStatus: '/api/metrics/orders-status',
  inventoryAlerts: '/api/alerts',
  payoutPreview: '/api/payouts/preview',
  
  // Product Management Endpoints
  products: '/api/products',
  productById: (id) => `/api/products/${id}`,
  createProduct: '/api/products',
  updateProduct: (id) => `/api/products/${id}`,
  deleteProduct: (id) => `/api/products/${id}`,
  productCategories: '/api/products/categories',
  productCsvTemplate: '/api/products/csv-template',
  productUploadCsv: '/api/products/upload-csv',
  productNextSku: (vendorId, categoryCode) => `/api/products/next-sku/${vendorId}/${categoryCode}`,
  productPrice: (id) => `/api/products/${id}/price`,
  productPriceRequest: (id) => `/api/products/${id}/price-request`,
  uploadProductImage: '/api/upload/product-image',
  productBulkUpdate: '/api/products/bulk-update',
  productBulkDelete: '/api/products/bulk-delete',
  
  // Categories Endpoints
  categories: '/api/categories',
  categoryById: (id) => `/api/categories/${id}`,
  createCategory: '/api/categories',
  updateCategory: (id) => `/api/categories/${id}`,
  deleteCategory: (id) => `/api/categories/${id}`,
  
  // Inventory Management Endpoints
  inventoryReserve: '/api/inventory/reserve',
  inventoryCommit: '/api/inventory/commit',
  inventoryRelease: '/api/inventory/release',
  inventoryAvailability: (productId, zone) => `/api/inventory/availability/${productId}/${zone}`,
  inventoryVendorSummary: (vendorId) => `/api/inventory/vendor/${vendorId}/summary`,
  inventoryUpdateStock: (vendorId, productId) => `/api/inventory/vendor/${vendorId}/product/${productId}/stock`,
  inventoryReservations: '/api/inventory/reservations',
  inventoryAnalytics: '/api/inventory/analytics/performance',
  inventoryPriorityQueue: (productId, zone) => `/api/inventory/priority-queue/${productId}/${zone}`,
  inventoryPartialFulfillment: '/api/inventory/partial-fulfillment',
  inventoryCleanupExpired: '/api/inventory/cleanup-expired',
  inventorySummary: '/api/inventory/summary',
  inventoryStockAdjust: (productId) => `/api/inventory/${productId}/adjust`,
  inventoryBulkUpdate: '/api/inventory/bulk-update',
  inventoryForecast: (vendorId) => `/api/inventory/vendor/${vendorId}/forecast`,
  inventoryMovements: (vendorId) => `/api/inventory/vendor/${vendorId}/movements`,
  
  // Order Management Endpoints
  vendorOrders: (vendorId) => `/api/orders/vendor/${vendorId}`,
  orderById: (id) => `/api/orders/${id}`,
  updateOrderStatus: (id) => `/api/orders/${id}/status`,
  exportOrders: '/api/orders/export',
  orderNotifications: (vendorId) => `/api/orders/notifications/${vendorId}`,
  orderTracking: (id) => `/api/orders/${id}/tracking`,
  orderCancel: (id) => `/api/orders/${id}/cancel`,
  orderRefund: (id) => `/api/orders/${id}/refund`,
  orderHistory: (vendorId) => `/api/orders/vendor/${vendorId}/history`,
  
  // Payout Management Endpoints
  payoutSummary: '/api/payouts/vendor/summary',
  payoutPaymentMethods: '/api/payouts/vendor/payment-methods',
  addPaymentMethod: '/api/payouts/vendor/payment-methods',
  updatePaymentMethod: (id) => `/api/payouts/vendor/payment-methods/${id}`,
  requestPayout: '/api/payouts/vendor/request',
  payoutHistory: '/api/payouts/vendor/history',
  payoutTransactions: '/api/payouts/vendor/transactions',
  payoutNotifications: '/api/payouts/vendor/notifications',
  markPayoutNotificationRead: (id) => `/api/payouts/vendor/notifications/${id}/read`,
  payoutWalletTransactions: '/api/payouts/vendor/wallet/transactions',
  payoutStatement: (payoutId, format) => `/api/payouts/vendor/history/${payoutId}/statement?format=${format}`,
  payoutEarningsBreakdown: (period) => `/api/payouts/vendor/earnings?period=${period}`,
  
  // KYC Management Endpoints (these use /api/ as they're shared across portals)
  kycDashboard: (id) => `/api/kyc/dashboard/${id}`,
  kycDocuments: (id) => `/api/kyc/documents/${id}`,
  kycUpload: (id) => `/api/kyc/upload/${id}`,
  kycDocumentDelete: (docId) => `/api/kyc/document/${docId}`,
  kycDocumentDownload: (docId) => `/api/kyc/document/${docId}/download`,
  kycNotifications: (id) => `/api/kyc/notifications/${id}`,
  kycNotificationRead: (notificationId) => `/api/kyc/notifications/${notificationId}/read`,
  kycTemplates: '/api/kyc/templates',
  kycOverallStatus: (id) => `/api/kyc/status/${id}`,
  kycAuditTrail: (id) => `/api/kyc/audit/${id}`,
  kycResubmit: (id) => `/api/kyc/resubmit/${id}`,
  kycProgress: (id) => `/api/kyc/progress/${id}`,
  
  // Notifications Endpoints (shared across portals)
  notifications: '/api/notifications',
  notificationUnreadCount: '/api/notifications/unread-count',
  markNotificationRead: (id) => `/api/notifications/${id}/read`,
  markAllNotificationsRead: '/api/notifications/mark-all-read',
  notificationSettings: '/api/notifications/settings',
  updateNotificationSettings: '/api/notifications/settings',
  
  // Reports & Analytics Endpoints
  reportsSummary: '/api/reports/summary',
  reportsTrend: (granularity, days) => `/api/reports/trend?granularity=${granularity}&days=${days}`,
  reportsTopVendors: (limit = 10) => `/api/reports/top-vendors?limit=${limit}`,
  reportsTopProducts: (limit = 10) => `/api/reports/top-products?limit=${limit}`,
  reportsExport: (type) => `/api/reports/export/${type}`,
  reportsVendorPerformance: (vendorId) => `/api/reports/vendor/${vendorId}/performance`,
  reportsSalesAnalytics: (vendorId) => `/api/reports/vendor/${vendorId}/sales`,
  reportsInventoryAnalytics: (vendorId) => `/api/reports/vendor/${vendorId}/inventory`,
  
  // Delivery & Logistics Endpoints
  deliveryPartners: '/api/delivery/partners',
  deliveryTracking: (orderId) => `/api/delivery/tracking/${orderId}`,
  deliveryHistory: (vendorId) => `/api/delivery/vendor/${vendorId}/history`,
  deliveryRatings: (vendorId) => `/api/delivery/vendor/${vendorId}/ratings`,
  deliveryZones: '/api/delivery/zones',
  deliveryEstimate: '/api/delivery/estimate',
  
  // Admin Endpoints (these remain /api/ as they're for admin portal access)
  adminDashboardSummary: '/api/admin/dashboard/summary',
  adminInventorySummary: '/api/admin/inventory/summary',
  adminVendors: '/api/admin/vendors',
  adminPayouts: '/api/admin/payouts',
  adminKycDocuments: '/api/admin/kyc-documents',
  adminKycDocumentStatus: (docId) => `/api/admin/kyc-documents/${docId}/status`,
  adminKycRunOCR: (docId) => `/api/admin/kyc/${docId}/ocr`,
  adminKycVerify: (docId) => `/api/admin/kyc/${docId}/verify`,
  adminKycAudit: '/api/admin/kyc-audit',
  adminKycDownload: (docId) => `/api/admin/documents/${docId}/download`,
  adminKycSummary: '/api/admin/kyc-summary',
  
  // Admin Reports (remain /api/ for admin access)
  adminReport_VendorsActiveInactive: '/api/admin/reports/vendors/active-inactive',
  adminReport_VendorPerformance: '/api/admin/reports/vendors/performance',
  adminReport_VendorApprovalHistory: '/api/admin/reports/vendors/approval-history',
  adminReport_ProductSalesRevenue: '/api/admin/reports/products/sales-revenue',
  adminReport_LowStock: '/api/admin/reports/products/low-stock',
  adminReport_TopLeastProducts: '/api/admin/reports/products/top-least',
  adminReport_ProductPriceHistory: '/api/admin/reports/products/price-history',
  adminReport_StockMovements: '/api/admin/reports/inventory/stock-movements',
  adminReport_AvailableVsReserved: '/api/admin/reports/inventory/available-vs-reserved',
  adminReport_HistoricalStock: '/api/admin/reports/inventory/historical-stock',
  adminReport_OrdersByStatus: '/api/admin/reports/orders/by-status',
  adminReport_PaymentStatus: '/api/admin/reports/orders/payment-status',
  adminReport_OrdersAggregate: '/api/admin/reports/orders/aggregate',
  adminReport_KycVerificationStatus: '/api/admin/reports/kyc/verification-status',
  adminReport_KycExpiringDocuments: '/api/admin/reports/kyc/expiring-documents',
  adminReport_KycAuditLog: '/api/admin/reports/kyc/audit-log',
  adminReport_Export: '/api/admin/reports/export/:type',
  
  // Admin Operations (remain /api/ for admin access)
  adminOrders: '/api/admin/orders',
  adminOrderById: (id) => `/api/admin/orders/${id}`,
  adminProducts: '/api/admin/products',
  adminProductPriceHistory: (id) => `/api/admin/products/${id}/price-history`,
  adminVendorsList: '/api/admin/vendors',
  adminDocApprove: (docId) => `/api/admin/documents/${docId}/approve`,
  adminDocReject: (docId) => `/api/admin/documents/${docId}/reject`,
  adminDocOCR: (docId) => `/api/admin/documents/${docId}/ocr`,
  adminDocVerify: (docId) => `/api/admin/documents/${docId}/verify`,
  adminDocDelete: (docId) => `/api/admin/documents/${docId}/delete`,
  
  // Payments (shared endpoints, remain /api/)
  paymentsList: '/api/payments',
  paymentById: (id) => `/api/payments/${id}`,
  paymentRefund: (id) => `/api/payments/${id}/refund`,
  paymentReprocess: (id) => `/api/payments/${id}/reprocess`,
  paymentsBulk: '/api/payments/bulk',
  paymentMethods: '/api/payments/methods',
  paymentGateways: '/api/payments/gateways',
  paymentHistory: (vendorId) => `/api/payments/vendor/${vendorId}/history`,
  
  // Supplier Management Endpoints
  suppliers: '/api/suppliers',
  supplierById: (id) => `/api/suppliers/${id}`,
  createSupplier: '/api/suppliers',
  updateSupplier: (id) => `/api/suppliers/${id}`,
  deleteSupplier: (id) => `/api/suppliers/${id}`,
  supplierProducts: (id) => `/api/suppliers/${id}/products`,
  supplierOrders: (id) => `/api/suppliers/${id}/orders`,
  
  // Invoice Management Endpoints
  invoices: '/api/invoices',
  invoiceById: (id) => `/api/invoices/${id}`,
  createInvoice: '/api/invoices',
  updateInvoice: (id) => `/api/invoices/${id}`,
  invoiceDownload: (id) => `/api/invoices/${id}/download`,
  invoiceHistory: (vendorId) => `/api/invoices/vendor/${vendorId}`,
  invoiceTemplates: '/api/invoices/templates',
  
  // Settings & Configuration Endpoints
  vendorSettings: (vendorId) => `/api/vendors/${vendorId}/settings`,
  updateVendorSettings: (vendorId) => `/api/vendors/${vendorId}/settings`,
  businessHours: (vendorId) => `/api/vendors/${vendorId}/business-hours`,
  updateBusinessHours: (vendorId) => `/api/vendors/${vendorId}/business-hours`,
  
  // File Upload Endpoints
  uploadFile: '/api/upload/file',
  uploadImage: '/api/upload/image',
  uploadDocument: '/api/upload/document',
  deleteFile: (fileId) => `/api/upload/file/${fileId}`,
  
  // Search & Filter Endpoints
  searchProducts: '/api/search/products',
  searchOrders: '/api/search/orders',
  searchCustomers: '/api/search/customers',
  filterOptions: '/api/filters/options',
};

// Helper functions for common API operations
export const apiHelpers = {
  // Get current vendor ID from localStorage
  getCurrentVendorId: () => {
    try {
      const vendorData = localStorage.getItem('vendorData');
      if (vendorData) {
        const parsed = JSON.parse(vendorData);
        return parsed.id || parsed.vendor_id;
      }
    } catch (e) {
      console.warn('Failed to get vendor ID:', e);
    }
    return null;
  },

  // Check if user is authenticated
  isAuthenticated: () => {
    const token = localStorage.getItem('token') || localStorage.getItem('vendorToken');
    const isLoggedIn = localStorage.getItem('isLoggedIn') === 'true';
    return Boolean(token && isLoggedIn);
  },

  // Get user type
  getUserType: () => {
    return localStorage.getItem('userType') || 'vendor';
  },

  // Format API response for consistent error handling
  handleApiResponse: async (response) => {
    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      throw new Error(errorData.message || `HTTP ${response.status}: ${response.statusText}`);
    }
    return response.json();
  },

  // Make authenticated GET request
  get: async (endpoint, params = {}) => {
    const url = new URL(`${API_BASE_URL}${endpoint}`);
    Object.keys(params).forEach(key => {
      if (params[key] !== undefined && params[key] !== null) {
        url.searchParams.append(key, params[key]);
      }
    });
    
    const response = await apiClient.get(url.pathname + url.search);
    return response.data;
  },

  // Make authenticated POST request
  post: async (endpoint, data = {}) => {
    const response = await apiClient.post(endpoint, data);
    return response.data;
  },

  // Make authenticated PUT request
  put: async (endpoint, data = {}) => {
    const response = await apiClient.put(endpoint, data);
    return response.data;
  },

  // Make authenticated DELETE request
  delete: async (endpoint) => {
    const response = await apiClient.delete(endpoint);
    return response.data;
  },

  // Upload file with progress tracking
  uploadFile: async (endpoint, file, onProgress = null) => {
    const formData = new FormData();
    formData.append('file', file);
    
    const config = {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
      timeout: 300000, // 5 minutes for file uploads
    };
    
    if (onProgress) {
      config.onUploadProgress = (progressEvent) => {
        const percentCompleted = Math.round((progressEvent.loaded * 100) / progressEvent.total);
        onProgress(percentCompleted);
      };
    }
    
    const response = await uploadClient.post(endpoint, formData, config);
    return response.data;
  },

  // Batch API requests
  batch: async (requests) => {
    const promises = requests.map(({ method, endpoint, data }) => {
      switch (method.toLowerCase()) {
        case 'get':
          return apiHelpers.get(endpoint, data);
        case 'post':
          return apiHelpers.post(endpoint, data);
        case 'put':
          return apiHelpers.put(endpoint, data);
        case 'delete':
          return apiHelpers.delete(endpoint);
        default:
          throw new Error(`Unsupported method: ${method}`);
      }
    });
    
    return Promise.allSettled(promises);
  },

  // Retry failed requests
  retry: async (fn, maxRetries = 3, delay = 1000) => {
    for (let i = 0; i < maxRetries; i++) {
      try {
        return await fn();
      } catch (error) {
        if (i === maxRetries - 1) throw error;
        await new Promise(resolve => setTimeout(resolve, delay * Math.pow(2, i)));
      }
    }
  },
};

export default apiClient;


